/**
 * Main entry point for FlowRide Frontend Application
 * This file initializes the React application with custom touch handling
 * and service worker management for a mobile-first experience.
 */

import { createRoot } from "react-dom/client";
import App from "./App";
import { ConsoleInterceptor } from "./services/ConsoleInterceptor";
import { fetchFrontendConfig, getOpenTelemetryConfig, isRemoteLoggingEnabled } from "./services/FrontendConfigApi";
import { initializeOpenTelemetryBrowser } from "./services/http/openTelemetryBrowser";
import { LoggerService } from "./services/LoggerService";
import { initializeServiceWorker } from "./sw-update";


// Initialize console interceptor only in development mode
if ( __IS_DEV__ ) {
    ConsoleInterceptor.patch();
}

const isEditableTarget = ( target: EventTarget | null ): boolean => {
    if ( target instanceof HTMLElement === false ) {
        return false;
    }

    const editableSelector = "input, textarea, select, [contenteditable='true']";

    return target.closest( editableSelector ) !== null;
};

const disableZoom = (): void => {
    const preventDefault = ( event: Event ): void => {
        event.preventDefault();
    };

    const listenerOptions: AddEventListenerOptions = { "passive": false };
    document.addEventListener( "gesturestart", preventDefault, listenerOptions );
    document.addEventListener( "gesturechange", preventDefault, listenerOptions );
    document.addEventListener( "gestureend", preventDefault, listenerOptions );

    let lastTouchEndTimestamp = 0;
    const handleTouchEnd = ( event: TouchEvent ): void => {
        const now = Date.now();
        const timeSinceLastEnd = now - lastTouchEndTimestamp;
        if ( timeSinceLastEnd <= 300 && isEditableTarget( event.target ) === false ) {
            event.preventDefault();
        }
        lastTouchEndTimestamp = now;
    };

    document.addEventListener( "touchend", handleTouchEnd, listenerOptions );
};

/**
 * Mounts the React application once the runtime configuration has been loaded.
 */
function renderApplication(): void {
    const container = document.getElementById( "root" );
    if ( container !== null ) {
        const root = createRoot( container );
        root.render( <App /> );
    }
}

/**
 * Boots the application: load public runtime configuration, initialize telemetry,
 * then render React. Telemetry and the UI depend on the backend config, so the
 * render is intentionally deferred until the first fetch attempt completes.
 */
async function bootstrap(): Promise<void> {
    disableZoom();

    /*
     * Load public runtime configuration from the backend. On failure, a safe
     * fallback is returned so the app still starts.
     */
    await fetchFrontendConfig();

    LoggerService.setBackendLoggingEnabled( isRemoteLoggingEnabled() );

    initializeOpenTelemetryBrowser( getOpenTelemetryConfig() );

    renderApplication();

    // Initialize service worker with intelligent update management
    void initializeServiceWorker();
}

void bootstrap();
